import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setAccessToken, setUnauthenticatedHandler } from '@/lib/api';
import type { AuthResponse, User } from '@/lib/types';

interface AuthContextValue {
  user: User | null;
  /** True until the silent sign-in attempt on first load has settled. */
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Adopts a session established elsewhere, such as the OAuth callback. */
  adopt: (auth: AuthResponse) => void;
  /**
   * Replaces the signed-in user after they edit their own profile.
   *
   * Not `adopt`: the identity has not changed, and clearing the query cache here would throw away
   * the reference catalogue and the seller's own listings for a change of opening hours.
   */
  applyProfile: (next: User) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  /*
    Every cached answer belongs to whoever asked it.

    The query client is created once for the page and outlives any single session, and none of the
    keys carry an account: `inbox`, `unread-messages`, `favourites`, `my-listings` are just those
    words. Without this, signing in as someone else on the same tab shows them the previous
    account's conversations - names and message previews included - for as long as the entries stay
    fresh. Emptying the cache whenever the identity changes is the only version of this that cannot
    be got wrong by somebody adding a query later.

    The identity is compared rather than assumed, so the silent refresh that restores an existing
    session on load does not throw away the reference catalogue that is already in flight beside it.
  */
  const identity = useRef<string | null>(null);

  const becomes = useCallback(
    (next: User | null) => {
      if (identity.current !== (next?.id ?? null)) {
        identity.current = next?.id ?? null;
        queryClient.clear();
      }

      setUser(next);
    },
    [queryClient],
  );

  const adopt = useCallback(
    (auth: AuthResponse) => {
      setAccessToken(auth.accessToken);
      becomes(auth.user);
    },
    [becomes],
  );

  useEffect(() => {
    // Signals from the api layer that a refresh failed, so the UI stops claiming to be signed in.
    setUnauthenticatedHandler(() => becomes(null));
  }, [becomes]);

  useEffect(() => {
    // The access token is deliberately not persisted, so on every load the only evidence of a
    // session is the httpOnly refresh cookie. Redeeming it here is what makes a page refresh keep
    // the user signed in without the token ever being readable by script.
    let cancelled = false;

    (async () => {
      try {
        const auth = await api<AuthResponse>('/api/auth/refresh', { method: 'POST', body: {} });

        if (!cancelled) {
          // Restoring, not adopting: the page has only just loaded, so there is nobody else's data
          // to discard, and clearing here would refetch the reference catalogue for nothing.
          identity.current = auth.user.id;
          setAccessToken(auth.accessToken);
          setUser(auth.user);
        }
      } catch {
        /* No cookie, or it has expired. Being signed out is the correct outcome, not an error. */
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      adopt(await api<AuthResponse>('/api/auth/login', { method: 'POST', body: { email, password } }));
    },
    [adopt],
  );

  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      adopt(
        await api<AuthResponse>('/api/auth/register', {
          method: 'POST',
          body: { email, password, displayName },
        }),
      );
    },
    [adopt],
  );

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST', body: {} });
    } finally {
      // Cleared regardless. A sign-out that appears to fail because the network blipped would leave
      // someone looking at a signed-in page on a shared computer.
      setAccessToken(null);
      becomes(null);
    }
  }, [becomes]);

  const applyProfile = useCallback((next: User) => setUser(next), []);

  const value = useMemo(
    () => ({ user, isLoading, login, register, logout, adopt, applyProfile }),
    [user, isLoading, login, register, logout, adopt, applyProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
